import { useState, useEffect } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { db, auth } from "../../firebase";

/**
 * EditProfileModal
 *
 * Modal para editar el perfil del usuario:
 * - Nombre, cumpleaños, idioma, país, color
 * - Moneda preferida
 * - Links de pago (MercadoPago, PayPal)
 * - Lista de Amazon con selector de wishlus donde mostrarla
 * - Cambiar email o contraseña
 * - Eliminar cuenta
 */
export default function EditProfileModal({
  user, userProfile, setUserProfile,
  lang, darkMode,
  editProfileForm, setEditProfileForm,
  activeTab, setActiveTab,
  setShowEditProfile,
  setCurrentFamily, setUserFamilies, setMembers, setScreen,
  saveProfile,
  showToast, tr,
}) {
  // ── Wishlists del usuario para el selector de Amazon ─────
  const [wishlists, setWishlists] = useState([]);

  useEffect(() => {
    const ids = userProfile?.wishlists || [];
    if (ids.length === 0) { setWishlists([]); return; }
    Promise.all(ids.map((id) => getDoc(doc(db, "wishlists", id))))
      .then((snaps) => setWishlists(snaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }))));
  }, [userProfile?.wishlists]);

  // ── Toggle de visibilidad por wishlist ────────────────────
  const toggleWishlistVisibility = (wid) => {
    const current = editProfileForm.amazonWishlistVisibility || [];
    const next = current.includes(wid)
      ? current.filter((id) => id !== wid)
      : [...current, wid];
    setEditProfileForm((f) => ({ ...f, amazonWishlistVisibility: next }));
  };

  const eliminarCuenta = async () => {
    if (!window.confirm(
      lang === "es"
        ? "¿Eliminar tu cuenta permanentemente? Esta acción no se puede deshacer."
        : "Delete your account permanently? This cannot be undone."
    )) return;

    try {
      for (const fid of (userProfile?.families || [])) {
        const fSnap = await getDoc(doc(db, "families", fid));
        if (fSnap.exists()) {
          const newIds = (fSnap.data().memberIds || []).filter((id) => id !== user.uid);
          await updateDoc(doc(db, "families", fid), { memberIds: newIds });
        }
      }
      for (const wid of (userProfile?.wishlists || [])) {
        const { deleteDoc } = await import("firebase/firestore");
        await deleteDoc(doc(db, "wishlists", wid));
      }
      const { deleteDoc } = await import("firebase/firestore");
      await deleteDoc(doc(db, "users", user.uid));
      await user.delete();
      try {
        ["whislu_profile","whislu_family","whislu_members","whislu_families"]
          .forEach((k) => localStorage.removeItem(k));
      } catch (e) {}
      setShowEditProfile(false);
      setUserProfile(null);
      setCurrentFamily(null);
      setUserFamilies([]);
      setMembers([]);
      setScreen("family");
      setActiveTab("wishlu");
      showToast(lang === "es" ? "✅ Cuenta eliminada" : "✅ Account deleted");
    } catch (e) {
      showToast(
        e.code === "auth/requires-recent-login"
          ? (lang === "es"
              ? "Por seguridad, cierra sesión, vuelve a entrar y elimina tu cuenta."
              : "For security, sign out, sign back in, then delete your account.")
          : (lang === "es" ? "Error al eliminar cuenta." : "Error deleting account.")
      );
    }
  };

  const cambiarEmail = async () => {
    try {
      await user.verifyBeforeUpdateEmail(editProfileForm.newEmail);
      showToast(lang === "es"
        ? "📧 Revisa tu nuevo correo para confirmar el cambio"
        : "📧 Check your new email to confirm the change");
      setEditProfileForm((f) => ({ ...f, newEmail: "" }));
    } catch (e) {
      showToast(
        e.code === "auth/requires-recent-login"
          ? (lang === "es"
              ? "Por seguridad cierra sesión, vuelve a entrar y luego cambia tu correo"
              : "For security, sign out, sign back in, then change your email")
          : (lang === "es" ? "Error al cambiar correo" : "Error changing email")
      );
    }
  };

  const cambiarPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, userProfile?.email);
      showToast(lang === "es" ? "📧 Correo de cambio de contraseña enviado" : "📧 Password reset email sent");
    } catch (e) {
      showToast(lang === "es" ? "Error al enviar correo" : "Error sending email");
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowEditProfile(false); }}>
      <div className="modal" style={{ overflowY: "auto", maxHeight: "88vh" }}>
        <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 18, marginBottom: 18 }}>{tr("editProfile")}</h3>

{/* Color de perfil */}
        <div className="campo">
          <label className="campo-label">{tr("profileColor")}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="color" value={editProfileForm.color}
              onChange={(e) => setEditProfileForm((f) => ({ ...f, color: e.target.value }))}
              style={{ width: 48, height: 48, border: "none", borderRadius: 10, cursor: "pointer" }} />
            <div className="miembro-avatar" style={{ background: editProfileForm.color, width: 48, height: 48, fontSize: 20, margin: 0 }}>
              {editProfileForm.name ? editProfileForm.name.charAt(0).toUpperCase() : "?"}
            </div>
            <span style={{ fontSize: 13, color: "#999" }}>{tr("preview")}</span>
          </div>
        </div>

        {/* Nombre */}
        <div className="campo">
          <label className="campo-label">{tr("yourName")}</label>
          <input className="campo-input" value={editProfileForm.name}
            onChange={(e) => setEditProfileForm((f) => ({ ...f, name: e.target.value }))} />
        </div>

        {/* Cumpleaños */}
        <div className="campo">
          <label className="campo-label">{tr("birthday")}</label>
          <input className="campo-input" placeholder="DD/MM/YYYY" value={editProfileForm.birthday} maxLength={10}
            onChange={(e) => {
              let v = e.target.value.replace(/\D/g, "");
              if (v.length >= 3 && v.length <= 4) v = v.slice(0,2) + "/" + v.slice(2);
              else if (v.length >= 5) v = v.slice(0,2) + "/" + v.slice(2,4) + "/" + v.slice(4,8);
              setEditProfileForm((f) => ({ ...f, birthday: v }));
            }} />
        </div>

        {/* Idioma, País y Moneda */}
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 4 }}>
  <div>
    <label className="campo-label">🗣️ {tr("language")}</label>
    <select className="campo-input" value={editProfileForm.language || "en"}
      onChange={(e) => setEditProfileForm((f) => ({ ...f, language: e.target.value }))}
      style={{ padding: "10px 6px", fontSize: 11 }}>
      <option value="en">🇬🇧 English</option>
      <option value="es">🇲🇽 Español</option>
      <option value="fi">🇫🇮 Suomi</option>
    </select>
  </div>
  <div>
    <label className="campo-label">🌍 {lang === "es" ? "País" : lang === "fi" ? "Maa" : "Country"}</label>
    <select className="campo-input" value={editProfileForm.country || ""}
      onChange={(e) => setEditProfileForm((f) => ({ ...f, country: e.target.value }))}
      style={{ padding: "10px 6px", fontSize: 11 }}>
      <option value="">—</option>
      <option value="MX">🇲🇽 México</option>
      <option value="FI">🇫🇮 Finland</option>
      <option value="ES">🇪🇸 España</option>
      <option value="US">🇺🇸 USA</option>
      <option value="AR">🇦🇷 Argentina</option>
      <option value="CO">🇨🇴 Colombia</option>
      <option value="CL">🇨🇱 Chile</option>
      <option value="PE">🇵🇪 Perú</option>
      <option value="VE">🇻🇪 Venezuela</option>
      <option value="EC">🇪🇨 Ecuador</option>
      <option value="GT">🇬🇹 Guatemala</option>
      <option value="CU">🇨🇺 Cuba</option>
      <option value="BO">🇧🇴 Bolivia</option>
      <option value="DO">🇩🇴 Dom. Rep.</option>
      <option value="HN">🇭🇳 Honduras</option>
      <option value="PY">🇵🇾 Paraguay</option>
      <option value="SV">🇸🇻 El Salvador</option>
      <option value="NI">🇳🇮 Nicaragua</option>
      <option value="CR">🇨🇷 Costa Rica</option>
      <option value="PA">🇵🇦 Panamá</option>
      <option value="UY">🇺🇾 Uruguay</option>
      <option value="GB">🇬🇧 UK</option>
      <option value="DE">🇩🇪 Germany</option>
      <option value="FR">🇫🇷 France</option>
      <option value="SE">🇸🇪 Sweden</option>
      <option value="NO">🇳🇴 Norway</option>
      <option value="DK">🇩🇰 Denmark</option>
      <option value="BR">🇧🇷 Brasil</option>
      <option value="CA">🇨🇦 Canada</option>
      <option value="AU">🇦🇺 Australia</option>
    </select>
  </div>
  <div>
    <label className="campo-label">💰 {tr("currencyLabel")}</label>
    <select className="campo-input" value={editProfileForm.currency || "USD"}
      onChange={(e) => setEditProfileForm((f) => ({ ...f, currency: e.target.value }))}
      style={{ padding: "10px 6px", fontSize: 11 }}>
      <option value="USD">$ USD</option>
      <option value="MXN">$ MXN</option>
      <option value="EUR">€ EUR</option>
    </select>
  </div>
</div>


        {/* Links de pago */}
        <div style={{ borderTop: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6"), marginTop: 4, paddingTop: 14, marginBottom: 4 }}>
          <p style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
            💸 {lang === "es" ? "Links de pago" : lang === "fi" ? "Maksulinkit" : "Payment links"}
          </p>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {/* MercadoPago */}
            <button onClick={() => setEditProfileForm((f) => ({ ...f, _payPanel: f._payPanel === "mp" ? null : "mp" }))}
              style={{ flex: 1, borderRadius: 14, padding: "10px 6px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, position: "relative",
                border: "2px solid " + (editProfileForm._payPanel === "mp" ? "#00b1ea" : editProfileForm.mercadopago ? "#27ae60" : (darkMode ? "#2e2e2e" : "#e0dbd2")),
                background: editProfileForm._payPanel === "mp" ? (darkMode ? "#0a2a35" : "#e8f7fd") : (darkMode ? "#1a1a1a" : "#fff"),
              }}>
              <svg width="32" height="32" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="10" fill="#00b1ea"/><text x="18" y="24" textAnchor="middle" fill="white" fontSize="14" fontWeight="800" fontFamily="Arial">MP</text></svg>
              <span style={{ fontSize: 10, fontWeight: 600, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>MercadoPago</span>
              {editProfileForm.mercadopago && <span style={{ position: "absolute", top: 5, right: 6, fontSize: 11 }}>✅</span>}
            </button>
            {/* PayPal */}
            <button onClick={() => setEditProfileForm((f) => ({ ...f, _payPanel: f._payPanel === "pp" ? null : "pp" }))}
              style={{ flex: 1, borderRadius: 14, padding: "10px 6px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, position: "relative",
                border: "2px solid " + (editProfileForm._payPanel === "pp" ? "#003087" : editProfileForm.paypal ? "#27ae60" : (darkMode ? "#2e2e2e" : "#e0dbd2")),
                background: editProfileForm._payPanel === "pp" ? (darkMode ? "#0a0f20" : "#eef2fb") : (darkMode ? "#1a1a1a" : "#fff"),
              }}>
              <svg width="32" height="32" viewBox="0 0 36 36" fill="none"><rect width="36" height="36" rx="10" fill="#003087"/><text x="18" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="800" fontFamily="Arial">Pay</text><text x="18" y="26" textAnchor="middle" fill="#009cde" fontSize="8" fontWeight="800" fontFamily="Arial">Pal</text></svg>
              <span style={{ fontSize: 10, fontWeight: 600, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>PayPal</span>
              {editProfileForm.paypal && <span style={{ position: "absolute", top: 5, right: 6, fontSize: 11 }}>✅</span>}
            </button>
          </div>
          {editProfileForm._payPanel === "mp" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: darkMode ? "#1a1a1a" : "#f0faff", border: "1px solid #00b1ea", borderRadius: 12, padding: "10px 14px", marginBottom: 4 }}>
              <input className="campo-input" autoFocus style={{ flex: 1, margin: 0, border: "none", background: "transparent", padding: 0, fontSize: 13 }}
                placeholder="usuario o https://link.mercadopago.com.mx/usuario"
                value={editProfileForm.mercadopago || ""}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, mercadopago: e.target.value }))}
                onBlur={(e) => {
                  let val = e.target.value.trim();
                  if (val && !val.startsWith("http")) {
                    val = "https://link.mercadopago.com.mx/" + val;
                    setEditProfileForm((f) => ({ ...f, mercadopago: val }));
                  }
                }} />
              {editProfileForm.mercadopago && (
                <button style={{ background: "none", border: "none", fontSize: 15, cursor: "pointer", color: "#aaa" }}
                  onClick={() => setEditProfileForm((f) => ({ ...f, mercadopago: "" }))}>✕</button>
              )}
            </div>
          )}
          {editProfileForm._payPanel === "pp" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: darkMode ? "#1a1a1a" : "#f0f4ff", border: "1px solid #003087", borderRadius: 12, padding: "10px 14px", marginBottom: 4 }}>
              <input className="campo-input" autoFocus style={{ flex: 1, margin: 0, border: "none", background: "transparent", padding: 0, fontSize: 13 }}
                placeholder="https://paypal.me/usuario"
                value={editProfileForm.paypal || ""}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, paypal: e.target.value }))} />
              {editProfileForm.paypal && (
                <button style={{ background: "none", border: "none", fontSize: 15, cursor: "pointer", color: "#aaa" }}
                  onClick={() => setEditProfileForm((f) => ({ ...f, paypal: "" }))}>✕</button>
              )}
            </div>
          )}
        </div>

       {/* ── Lista de Amazon ── */}
<div style={{ borderTop: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6"), marginTop: 4, paddingTop: 14, marginBottom: 4 }}>
  <p style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
    📦 {lang === "es" ? "Lista de Amazon" : lang === "fi" ? "Amazon-lista" : "Amazon Wishlist"}
  </p>

  {/* Input del link */}
  <div style={{ display: "flex", alignItems: "center", gap: 8, background: darkMode ? "#1a1a1a" : "#fffbf0", border: "1px solid #FF9900", borderRadius: 12, padding: "10px 14px", marginBottom: 8 }}>
    <span style={{ fontSize: 20 }}>📦</span>
    <input
      className="campo-input"
      style={{ flex: 1, margin: 0, border: "none", background: "transparent", padding: 0, fontSize: 13 }}
      placeholder="https://www.amazon.com/hz/wishlist/ls/..."
      value={editProfileForm.amazonWishlist || ""}
      onChange={(e) => setEditProfileForm((f) => ({ ...f, amazonWishlist: e.target.value }))}
    />
    {editProfileForm.amazonWishlist && (
      <button style={{ background: "none", border: "none", fontSize: 15, cursor: "pointer", color: "#aaa" }}
        onClick={() => setEditProfileForm((f) => ({ ...f, amazonWishlist: "" }))}>✕</button>
    )}
  </div>

  {/* Desplegable de wishlus */}
  {editProfileForm.amazonWishlist && wishlists.length > 0 && (
    <div style={{ border: "1px solid " + (darkMode ? "#2e2e2e" : "#e0dbd2"), borderRadius: 12, overflow: "hidden" }}>
      {/* Header desplegable */}
      <div
        onClick={() => setEditProfileForm((f) => ({ ...f, _amazonOpen: !f._amazonOpen }))}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer", background: darkMode ? "#1a1a1a" : "#faf8f5" }}>
        <span style={{ fontSize: 12, color: darkMode ? "#e8e2d9" : "#555" }}>
          {lang === "es" ? "Mostrar en:" : lang === "fi" ? "Näytä:" : "Show in:"}
          {" "}
          <span style={{ color: "#FF9900", fontWeight: 600 }}>
            {(editProfileForm.amazonWishlistVisibility || []).length > 0
              ? (editProfileForm.amazonWishlistVisibility || []).length + " " + (lang === "es" ? "seleccionadas" : "selected")
              : (lang === "es" ? "ninguna" : lang === "fi" ? "ei mitään" : "none")}
          </span>
        </span>
        <span style={{ color: "#aaa", fontSize: 12 }}>{editProfileForm._amazonOpen ? "▲" : "▼"}</span>
      </div>

      {/* Lista desplegada */}
      {editProfileForm._amazonOpen && (
        <div style={{ borderTop: "1px solid " + (darkMode ? "#2e2e2e" : "#e0dbd2") }}>
          {wishlists.map((wl) => {
            const isChecked = (editProfileForm.amazonWishlistVisibility || []).includes(wl.id);
            return (
              <div
                key={wl.id}
                onClick={() => toggleWishlistVisibility(wl.id)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", cursor: "pointer",
                  background: isChecked ? (darkMode ? "#2a2200" : "#fffbf0") : (darkMode ? "#1a1a1a" : "#fff"),
                  borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6"),
                }}>
                <span style={{ fontSize: 13, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>🎁 {wl.name}</span>
                <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  border: "2px solid " + (isChecked ? "#FF9900" : "#aaa"),
                  background: isChecked ? "#FF9900" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isChecked && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  )}
</div>

        {/* Cambiar email */}
        <div className="campo">
          <label className="campo-label">{lang === "es" ? "Cambiar correo" : lang === "fi" ? "Vaihda sähköposti" : "Change email"}</label>
          <input className="campo-input" type="email" placeholder={userProfile?.email}
            value={editProfileForm.newEmail || ""}
            onChange={(e) => setEditProfileForm((f) => ({ ...f, newEmail: e.target.value }))} />
          {editProfileForm.newEmail && editProfileForm.newEmail !== userProfile?.email && (
            <button className="btn btn-gris btn-sm" style={{ marginTop: 6 }} onClick={cambiarEmail}>
              {lang === "es" ? "Enviar verificación" : lang === "fi" ? "Lähetä vahvistus" : "Send verification"}
            </button>
          )}
        </div>

        {/* Cambiar contraseña */}
        <div style={{ marginBottom: 12 }}>
          <button className="btn btn-gris" style={{ width: "100%", fontSize: 13 }} onClick={cambiarPassword}>
            🔑 {lang === "es" ? "Cambiar contraseña" : lang === "fi" ? "Vaihda salasana" : "Change password"}
          </button>
        </div>

        {/* Guardar / Cancelar */}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="btn btn-dorado" style={{ flex: 2 }} onClick={saveProfile} disabled={!editProfileForm.name.trim()}>
            {tr("saveProfile")}
          </button>
          <button className="btn btn-gris" onClick={() => { setShowEditProfile(false); if (activeTab !== "profile") setActiveTab("profile"); }}>
            {tr("cancel")}
          </button>
        </div>

        {/* Eliminar cuenta */}
        <div style={{ textAlign: "center", marginTop: 20, paddingTop: 16, borderTop: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6") }}>
          <button style={{ background: "none", border: "none", fontSize: 11, color: "#aaa", cursor: "pointer", textDecoration: "underline" }}
            onClick={eliminarCuenta}>
            {lang === "es" ? "Eliminar mi cuenta" : lang === "fi" ? "Poista tilini" : "Delete my account"}
          </button>
        </div>

      </div>
    </div>
  );
}
